import { GLOBAL_E_URL, PUBLIC_URL } from '@/lib/const';
import { logGlobalEDebug } from '@/lib/globalEDebug';
import type { StorageHelper } from '@/lib/storage';
import { GLOBAL_ECART_ID_MAX_AGE } from '@/lib/storage';

export const GLOBAL_E_SCRIPT_ID = 'external-script';
export const GLOBAL_E_CHECKOUT_PATH = '/store/global-e/checkout/';

const GLOBAL_E_CHECKOUT_WITH_EXTRA_SEGMENT_PATTERN =
  /^\/store\/global-e\/checkout\/[^/]+\/?$/;

export const isGlobalECheckoutPath = (pathname: string | null | undefined) => {
  if (!pathname) {
    return false;
  }

  if (pathname === GLOBAL_E_CHECKOUT_PATH) {
    return true;
  }

  return GLOBAL_E_CHECKOUT_WITH_EXTRA_SEGMENT_PATTERN.test(pathname);
};

const getParentCookieDomain = (hostname: string): string | undefined => {
  const parts = hostname.split('.');

  if (parts.length < 2) {
    return undefined;
  }

  return `.${parts.slice(-2).join('.')}`;
};

const addHostnameCookieDomains = (
  domains: Set<string>,
  hostname: string,
): void => {
  domains.add(`.${hostname}`);

  const parentDomain = getParentCookieDomain(hostname);

  if (parentDomain) {
    domains.add(parentDomain);
  }
};

export const getGlobalECookieDomain = (): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return `.${window.location.hostname}`;
};

export const getGlobalECartIdCookieDomains = (): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const domains = new Set<string>();

  addHostnameCookieDomains(domains, window.location.hostname);

  try {
    addHostnameCookieDomains(domains, new URL(PUBLIC_URL).hostname);
  } catch {
    return Array.from(domains);
  }

  return Array.from(domains);
};

const decodeGlobalECartIdCookieValue = (raw: string): string => {
  try {
    return unescape(raw);
  } catch {
    return raw;
  }
};

export const GLOBAL_E_CHECKOUT_CART_ID_ATTRIBUTE = 'data-ge-cart-token';

export const applyGlobalECartIdToCheckoutContainer = (cartId: string): void => {
  if (typeof document === 'undefined' || !isUsableGlobalECartId(cartId)) {
    return;
  }

  const container = document.getElementById('ge-checkout');

  if (!container) {
    return;
  }

  container.setAttribute(GLOBAL_E_CHECKOUT_CART_ID_ATTRIBUTE, cartId);
};

export const listGlobalECartIdCookieValues = (): string[] => {
  if (typeof document === 'undefined') {
    return [];
  }

  return Array.from(
    document.cookie.matchAll(/(?:^|;)\s*GlobalECartId=([^;]*)/g),
    (match) => decodeGlobalECartIdCookieValue(match[1]),
  );
};

export const isUsableGlobalECartId = (value: unknown): value is string =>
  typeof value === 'string' &&
  value !== '' &&
  value !== 'undefined' &&
  value !== 'null';

const GLOBAL_E_CART_ID_COOKIE_OPTIONS = {
  maxAge: GLOBAL_ECART_ID_MAX_AGE,
  path: '/',
  sameSite: 'none' as const,
  secure: true,
} as const;

const writeGlobalECartIdCookies = (
  storageHelper: StorageHelper,
  cartId: string,
): void => {
  getGlobalECartIdCookieDomains().forEach((domain) => {
    storageHelper.setCookie('GlobalECartId', cartId, {
      ...GLOBAL_E_CART_ID_COOKIE_OPTIONS,
      domain,
    });
  });

  storageHelper.setCookie(
    'GlobalECartId',
    cartId,
    GLOBAL_E_CART_ID_COOKIE_OPTIONS,
  );
};

const readFirstGlobalECartIdCookieValue = (): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const match = document.cookie.match(/(?:^|;)\s*GlobalECartId=([^;]*)/);

  if (!match) {
    return undefined;
  }

  return decodeGlobalECartIdCookieValue(match[1]);
};

export const readGlobalECartIdLikeGlobalE = (): string | undefined => {
  const firstValue = readFirstGlobalECartIdCookieValue();

  if (!isUsableGlobalECartId(firstValue)) {
    return undefined;
  }

  return firstValue;
};

export const GLOBAL_E_CHECKOUT_UNDEFINED_SEGMENT =
  /\/store\/global-e\/checkout\/undefined(?:\/|$)/;

export const hasGlobalEApiCallQueryParam = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.location.search.includes('ge_ApiCall');
};

const tryDeleteWindowProperty = (property: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const descriptor = Object.getOwnPropertyDescriptor(window, property);

  if (!descriptor?.configurable) {
    return;
  }

  Reflect.deleteProperty(window, property);
};

export const isGlobalEMerchantScriptInitialized = (): boolean =>
  typeof window !== 'undefined' &&
  Boolean((window as Window & { GEMerchantUtils?: unknown }).GEMerchantUtils);

export const stripUndefinedCartTokenFromCheckoutPath = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (hasGlobalEApiCallQueryParam()) {
    return false;
  }

  if (!GLOBAL_E_CHECKOUT_UNDEFINED_SEGMENT.test(window.location.pathname)) {
    return false;
  }

  const cleanPath = window.location.pathname.replace(
    /(\/store\/global-e\/checkout\/)undefined\/?/,
    '$1',
  );

  window.history.replaceState(
    null,
    '',
    `${cleanPath}${window.location.search}${window.location.hash}`,
  );

  return true;
};

export const buildGlobalEGetCartInfoPath = (cartToken: string): string =>
  `${GLOBAL_E_CHECKOUT_PATH}${cartToken}/`;

export const clearGlobalECartIdCookies = (
  storageHelper: StorageHelper,
): void => {
  const domains = getGlobalECartIdCookieDomains();

  logGlobalEDebug('cart-cookie:clear', {
    domains,
    before: listGlobalECartIdCookieValues(),
  });

  storageHelper.removeCookie('GlobalECartId', { path: '/' });
  domains.forEach((domain) => {
    storageHelper.removeCookie('GlobalECartId', { domain, path: '/' });
  });

  logGlobalEDebug('cart-cookie:cleared', {
    after: listGlobalECartIdCookieValues(),
  });
};

export const removeInvalidGlobalECartIdCookies = (
  storageHelper: StorageHelper,
): string | undefined => {
  const allValues = listGlobalECartIdCookieValues();

  if (allValues.length === 0) {
    return undefined;
  }

  const usableValue = allValues.find(isUsableGlobalECartId);

  if (usableValue && allValues.every((value) => value === usableValue)) {
    return usableValue;
  }

  if (!usableValue) {
    clearGlobalECartIdCookies(storageHelper);
    return undefined;
  }

  return syncGlobalECartIdCookie(storageHelper, usableValue);
};

export const syncGlobalECartIdCookie = (
  storageHelper: StorageHelper,
  cartId: string,
): string | undefined => {
  if (!isUsableGlobalECartId(cartId)) {
    return undefined;
  }

  const allValues = listGlobalECartIdCookieValues();
  const hasInvalidValue = allValues.some(
    (value) => !isUsableGlobalECartId(value),
  );
  const hasDuplicateValues = allValues.length > 1;
  const isAlreadySynced =
    allValues.length === 1 &&
    allValues[0] === cartId &&
    readGlobalECartIdLikeGlobalE() === cartId;

  if (isAlreadySynced) {
    logGlobalEDebug('cart-cookie:sync-skip', { cartId });
    applyGlobalECartIdToCheckoutContainer(cartId);
    return cartId;
  }

  if (hasInvalidValue || hasDuplicateValues) {
    clearGlobalECartIdCookies(storageHelper);
  }

  writeGlobalECartIdCookies(storageHelper, cartId);

  const readBack = readGlobalECartIdLikeGlobalE();

  if (!isUsableGlobalECartId(readBack) || readBack !== cartId) {
    logGlobalEDebug('cart-cookie:sync-failed', {
      cartId,
      readBack,
      allValues: listGlobalECartIdCookieValues(),
    });
    return undefined;
  }

  logGlobalEDebug('cart-cookie:sync-ok', {
    cartId: readBack,
    allValues: listGlobalECartIdCookieValues(),
  });

  applyGlobalECartIdToCheckoutContainer(readBack);

  return readBack;
};

export const removeInjectedGlobalEScripts = () => {
  if (typeof document === 'undefined') {
    return;
  }

  document.getElementById(GLOBAL_E_SCRIPT_ID)?.remove();

  if (!GLOBAL_E_URL) {
    return;
  }

  document
    .querySelectorAll(`script[src*="${GLOBAL_E_URL}/includes/js/"]`)
    .forEach((node) => node.remove());

  tryDeleteWindowProperty('glegem');
  tryDeleteWindowProperty('GEMerchantUtils');
};
